import React, {PropTypes} from 'react';
import {reduxForm, Field} from 'redux-form';
import Submitting from 'shared/Submitting.jsx';
import StandardField from 'shared/StandardField.jsx';
import StandardCheckboxField from 'shared/StandardCheckboxField.jsx';
import StandardSelectField from 'shared/StandardSelectField.jsx';
import scrollToFirstError from '../actions';

const requiredValue = value => value ? undefined : 'Required';

const BowlEventItemForm = ({
  handleSubmit,
  submitting,
  pristine,
  onCancel,
  onSubmit,
  ageOptions,
  ageBased,
  numberOfBowlers,
  isHandicapped,
  onIsHandicappedClick,
  handicapAppliedOptions,
  readOnly,
  availableLocationOptions,
  readOnlyEvent,
  readOnlyDivisions,
  onDelete
}) => (
  <div className="panel panel-primary animated fadeIn">
    <div className="panel-heading">

      <h4 className="panel-title">
        <small>Specify event details:</small>
      </h4>
    </div>

    <div className="panel-body">
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate>
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column'
          }}>
          <fieldset className="event-fieldset">
            <legend className="event-fieldset-legend">Basic Info</legend>
            <Field
              autoFocus
              name="name"
              type="text"
              component={StandardField}
              placeholder="Event Name"
              disabled={readOnly}
              tooltip="Use a descriptive name such as Team, Doubles, Singles"
              tooltipPosition="top"
            />
            <Field
              name="expenseFee"
              type="number"
              step="0.25"
              min="0"
              component={StandardField}
              placeholder="Expense Fee"
              tooltip="Enter the Total Expense Fee for the event, i.e. the individual fee per bowler times the number of bowlers on the team"
              tooltipPosition="top"
              disabled={readOnly || readOnlyEvent}
            />
            <Field
              name="lineageFee"
              type="number"
              step="0.25"
              min="0"
              component={StandardField}
              placeholder="Lineage Fee"
              tooltip="Enter the Total Linage Cost for the event, i.e. the individual fee per bowler times the number of bowlers on the team"
              tooltipPosition="top"
              disabled={readOnly || readOnlyEvent}
            />
            <Field
              name="prizeFee"
              type="number"
              step="0.25"
              min="0"
              component={StandardField}
              placeholder="Prize Fee"
              tooltip="Enter the Total Prize Fee for the event, i.e. the individual fee per bowler times the number of bowlers on the team"
              tooltipPosition="top"
              disabled={readOnly || readOnlyEvent}
            />
            <Field
              name="isHandicapped"
              component={StandardCheckboxField}
              label="Handicap Event"
              onCustomChange={(event) => onIsHandicappedClick(event.target.checked)}
              disabled={readOnly}
            />
            <Field
              name="locationId"
              component={StandardSelectField}
              options={availableLocationOptions}
              placeholder="Location"
              label="Location"
              simpleValue
              searchable={false}
              disabled={readOnly}
              validate={[ requiredValue ]}
            />
          </fieldset>

          { isHandicapped ?
          <fieldset className="event-fieldset">
            <legend className="event-fieldset-legend">Handicap Settings</legend>
            <h5>
              {'Enter percent and individual bowler average. Handicap is calculated as bowler or Team in the selected Tournament Management System.'}
            </h5>
            <div className="row">
              <div className="col-sm-4 col-xs-12">
                <Field
                  name="handicapPercentage"
                  component={StandardField}
                  label="Percentage"
                  type="number"
                  min="1"
                  max="100"
                  required={true}
                  disabled={readOnly}
                />
              </div>
              <div className="col-sm-4 col-xs-12">
                <Field
                  name="handicapAverage"
                  component={StandardField}
                  label="Average"
                  type="number"
                  min="0"
                  max="300"
                  required={true}
                  disabled={readOnly}
                />
              </div>
              <div className="col-sm-4 col-xs-12">
                <Field
                  name="handicapApplied"
                  component={StandardSelectField}
                  label="Applies As"
                  placeholder="Applies As"
                  options={handicapAppliedOptions}                  
                  disabled={readOnly}
                />
              </div>  
            </div>
            <Field
              name="allowNegativeHandicap"
              component={StandardCheckboxField}
              label="Allow Negative Handicap"
              disabled={readOnly}
            />            
          </fieldset>
          : null
          }

          <fieldset className="event-fieldset">
            <legend className="event-fieldset-legend">Roster Info</legend>
            <Field
              name="numberOfBowlers"
              type="number"
              step="1"
              min="0"
              component={StandardField}
              placeholder="Bowlers Per Team"
              disabled={readOnly || readOnlyEvent}
            />
            <Field
              name="gamesPerSeries"
              type="number"
              step="1"
              min="0"
              component={StandardField}
              placeholder="Games Per Series"
              disabled={readOnly}
            />
            <Field
              name="entriesPerLanePair"
              type="number"
              step="1"
              min="0"
              component={StandardField}
              placeholder="Teams/Lane Pair"
              disabled={readOnly}
            />
          </fieldset>

          <fieldset className="event-fieldset">
            <legend className="event-fieldset-legend">Entry Info</legend>
            <Field
              name="maxEntriesPerBowler"
              type="number"
              step="1"
              min="1"
              component={StandardField}
              placeholder="Max Entries/Bowler"
              disabled={readOnly}
            />
            <Field
              name="numberOfBowlersThatMustBeDifferentPerTeam"
              type="number"
              step="1"
              min="0"
              component={StandardField}
              placeholder="Number Of Bowlers That Must Be Different/Team"
              disabled={readOnly}
            />
          </fieldset>

          <fieldset className="event-fieldset">
            <legend className="event-fieldset-legend">Division Settings</legend>
            <h5>
              {'Identify what will determine your divisions, if applicable.'}
            </h5>
            <Field
              name="averageBased"
              component={StandardCheckboxField}
              label="Include Average divisions"
              disabled={readOnly || readOnlyDivisions}
            />
            <Field
              name="ageBased"
              component={StandardCheckboxField}
              label="Include Age divisions"
              disabled={readOnly || readOnlyDivisions}
            />
            { ageBased ?
              <fieldset className="event-fieldset">
                { numberOfBowlers > 1 ?
                  <Field
                    name="ageAppliesTo"
                    placeholder="Age Option"
                    component={StandardSelectField}
                    options={ageOptions}
                    simpleValue
                    searchable
                    matchPos="start"
                    disabled={readOnly || readOnlyDivisions}
                    tooltip="Oldest Member typically used for Youth Events and Youngest Member for Senior events"
                    tooltipPosition="top"
                  /> 
                : null
                }
                <Field
                  name="ageVerificationDate"
                  type="date"
                  todayHighlight={true}
                  component={StandardField}
                  placeholder="Date on which the bowler needs to be the specified age"
                  label="Event Specific Age as of Date"
                  disabled={readOnly || readOnlyDivisions}
                  tooltip="Used to determine the starting date in an age specific event.  Adding date here applies the age to only this event. Leaving blank will inherit the Tournament wide age as of date"
                  tooltipPosition="top"
                />
              </fieldset>
            : null
            }
            <Field
              name="genderBased"
              component={StandardCheckboxField}
              label="Include Gender divisions"
              disabled={readOnly || readOnlyDivisions}
            />
          </fieldset>

          <div className="form-group text-right">
            <button
              type="button"
              className="btn btn-link"
              disabled={submitting}
              onClick={onCancel}
            >Cancel</button>
            {` `}
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={pristine || submitting}
            >{submitting ? <Submitting /> : <i className="fa fa-floppy-o" />} {`Save`}</button>
          </div>
        </div>
      </form>
    </div>
  </div>
);

BowlEventItemForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  pristine: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  ageOptions: PropTypes.arrayOf(PropTypes.object),
  ageBased: PropTypes.bool,
  ageVerificationDate: PropTypes.string,
  numberOfBowlers: PropTypes.number,
  isHandicapped: PropTypes.bool,
  onIsHandicappedClick: PropTypes.func,
  handicapPercentage: PropTypes.number,
  handicapAverage: PropTypes.number,
  handicapAppliedOptions: PropTypes.arrayOf(PropTypes.object),
  readOnly: PropTypes.bool,
  availableLocationOptions: PropTypes.arrayOf(PropTypes.object),
  readOnlyEvent: PropTypes.bool,
  readOnlyDivisions: PropTypes.bool,
  onDelete: PropTypes.func
};

export default reduxForm({
  form: 'eventItemForm',
  onSubmitFail: (errors) => scrollToFirstError(errors)
})(BowlEventItemForm);



// WEBPACK FOOTER //
// ./src/features/tournament/events/BowlEventItemForm.jsx